import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, ShoppingCart, Eye, Clock, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../ui/carousel';

const STORAGE_KEY = 'astro_recently_viewed';

export const RecentlyViewed = () => {
  const [products, setProducts] = useState([]);
  const { addToCart } = useCart();

  useEffect(() => {
    loadRecentlyViewed();
    
    // Listen for storage changes (when user views a product)
    const handleStorageChange = () => {
      loadRecentlyViewed();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('recentlyViewedUpdated', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('recentlyViewedUpdated', handleStorageChange);
    };
  }, []);

  const loadRecentlyViewed = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setProducts(parsed.slice(0, 6));
      }
    } catch (error) {
      console.error('Error loading recently viewed:', error);
    }
  };

  const getOptimizedImageUrl = (url) => {
    if (!url) return '';
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/\.(jpg|jpeg|png|webp)/, '_300x300.$1');
    }
    return url;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  const clearHistory = (e) => {
    e.preventDefault();
    localStorage.removeItem(STORAGE_KEY);
    setProducts([]);
    toast.success('Browsing history cleared');
  };

  // Don't render if no recently viewed products
  if (products.length === 0) {
    return null;
  }

  return (
    <section className="py-12 lg:py-16 bg-gradient-to-b from-amber-50/30 to-white" data-testid="recently-viewed-section">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-between items-center mb-8"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-serif text-2xl lg:text-3xl font-bold text-foreground">
                Recently <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600">Viewed</span>
              </h2>
              <p className="text-muted-foreground text-sm mt-0.5">
                Continue where you left off
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={clearHistory}
              className="text-muted-foreground hover:text-red-500 text-sm flex items-center gap-1 transition-colors"
              data-testid="clear-history-btn"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Clear History</span>
            </button>
            <Link
              to="/products"
              className="hidden sm:flex items-center gap-2 text-gold hover:text-gold-dark font-medium text-sm group"
              data-testid="view-all-products"
            >
              View All
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </motion.div>

        <Carousel
          opts={{
            align: 'start',
            loop: false,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-3">
            {products.map((product, index) => {
              const discount = product.originalPrice > product.price
                ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
                : 0;

              return (
                <CarouselItem key={product.id} className="pl-3 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5 xl:basis-1/6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="h-full"
                  >
                    <Link
                      to={`/product/${product.id}`}
                      className="group block bg-white rounded-xl overflow-hidden border border-amber-100 hover:border-amber-300 hover:shadow-lg transition-all duration-300 h-full"
                      data-testid={`recently-viewed-${product.id}`}
                    >
                      {/* Image */}
                      <div className="relative aspect-square overflow-hidden bg-amber-50">
                        <img
                          src={getOptimizedImageUrl(product.image)}
                          alt={product.name}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        
                        {/* Discount Badge */}
                        {discount > 0 && (
                          <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-green-500 text-white text-xs font-bold">
                            {discount}% OFF
                          </div>
                        )}

                        {/* Hover Actions */}
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={(e) => handleAddToCart(e, product)}
                            className="w-10 h-10 rounded-full bg-white text-gold hover:bg-gold hover:text-white transition-colors flex items-center justify-center shadow-lg"
                            title="Add to Cart"
                          >
                            <ShoppingCart className="w-5 h-5" />
                          </button>
                          <span className="w-10 h-10 rounded-full bg-white text-gold flex items-center justify-center shadow-lg">
                            <Eye className="w-5 h-5" />
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-3">
                        <h3 className="font-medium text-foreground text-sm line-clamp-2 mb-2 min-h-[2.5rem] group-hover:text-gold transition-colors">
                          {product.name}
                        </h3>

                        {/* Rating */}
                        <div className="flex items-center gap-1 mb-2">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 ${i < Math.floor(product.rating || 4.5) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            ({product.reviews || Math.floor(Math.random() * 500) + 50})
                          </span>
                        </div>

                        {/* Price */}
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gold text-base">
                            {formatPrice(product.price)}
                          </span>
                          {product.originalPrice > product.price && (
                            <span className="text-muted-foreground line-through text-xs">
                              {formatPrice(product.originalPrice)}
                            </span>
                          )}
                        </div>

                        {/* Add Button */}
                        <Button
                          onClick={(e) => handleAddToCart(e, product)}
                          size="sm"
                          className="w-full mt-3 gold-gradient text-white hover:opacity-90 text-xs h-8"
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </Link>
                  </motion.div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          {products.length > 4 && (
            <div className="hidden md:flex justify-center gap-2 mt-6">
              <CarouselPrevious className="static translate-y-0 bg-white border-amber-200 hover:bg-gold/10 hover:border-gold hover:text-gold" />
              <CarouselNext className="static translate-y-0 bg-white border-amber-200 hover:bg-gold/10 hover:border-gold hover:text-gold" />
            </div>
          )}
        </Carousel>
      </div>
    </section>
  );
};

export default RecentlyViewed;
